/**
 * PreFlightHUD.jsx - Pre-Send Checklist Component
 * 
 * Checklist Items Covered:
 * - C1: Pre-Flight Container
 * - C2: Neon Top Bar
 * - C3: Pre-Send Eyebrow
 * - C4: Pre-Send Title
 * - C5: Message Selected Check
 * - C6: Content Verified Check
 * - C7: Recipients Confirmed Check
 * - C8: Preview Before Send Check
 * - C9: Animated Halo Ring
 */

import React, { useEffect, useState } from 'react';
import { Check } from 'lucide-react';
import { styles, COLORS } from './messagingStyles';

const PreFlightHUD = ({
  hasTemplateSelected,
  hasContentVerified,
  hasRecipientsConfirmed,
  canPreview,
}) => {
  // C9: Animated halo state for pending items
  const [haloPhase, setHaloPhase] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setHaloPhase(prev => (prev + 1) % 100);
    }, 30);
    return () => clearInterval(interval);
  }, []);

  const getHaloStyle = (index) => {
    const phase = ((haloPhase + index * 25) % 100) / 100;
    const scale = 0.82 + (phase * 0.44);
    const opacity = 0.22 + (phase * 0.5);
    return {
      ...styles.preflightHaloRing,
      transform: `scale(${scale})`,
      opacity: opacity,
    };
  };

  const checklistItems = [
    { label: 'Message Selected', done: hasTemplateSelected },
    { label: 'Content Verified', done: hasContentVerified },
    { label: 'Recipients Confirmed', done: hasRecipientsConfirmed },
    { label: 'Preview before send', done: canPreview },
  ];

  return (
    <div style={styles.preflightHUD} data-testid="preflight-hud">
      {/* C2: Neon top border glow */}
      <div style={styles.preflightNeonTopBar} />
      
      {/* C3: Pre-Send Eyebrow */}
      <div style={styles.preflightEyebrow}>Pre-Send</div>
      
      {/* C4: Pre-Send Title */}
      <div style={styles.preflightTitle}>Pre-Send Checklist</div>
      
      {/* C5-C8: Checklist Items */}
      {checklistItems.map((item, i) => (
        <div key={i} style={styles.preflightRow} data-testid={`preflight-item-${i}`}>
          <div style={styles.preflightIndicatorWrap}>
            {item.done ? (
              // Completed state - green checkmark
              <div style={styles.preflightCheckCircle}>
                <Check size={11} color="#fff" strokeWidth={3} />
              </div>
            ) : (
              // Pending state - animated halo
              <div style={styles.preflightPendingWrap}>
                {/* C9: Animated Halo Ring */}
                <div style={getHaloStyle(i)} />
                <div style={styles.preflightPendingDot} />
              </div>
            )}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div 
              style={{
                ...styles.preflightItemLabel,
                ...(item.done ? styles.preflightItemLabelDone : {}),
              }}
            >
              {item.label}
            </div>
            <div 
              style={{
                ...styles.preflightItemStatus,
                ...(item.done ? styles.preflightItemStatusDone : {}),
              }}
            >
              {item.done ? 'Ready' : 'Pending'}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default PreFlightHUD;
